import { Link } from "react-router-dom";
import { ExternalLink, Layers } from "lucide-react";
import { GroupRefSlide } from "@shared/types";
import { SharedGroupRef } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScheduleEditor } from "@/components/admin/ScheduleEditor";
import { RowControlProps, RowHeader } from "@/components/admin/SlideCard";

interface GroupRefCardProps extends RowControlProps {
  slide: GroupRefSlide;
  group?: SharedGroupRef;
  onChange: (slide: GroupRefSlide) => void;
}

export function GroupRefCard({ slide, group, onChange, ...row }: GroupRefCardProps) {
  const name = group?.name ?? slide.name ?? "Shared group";
  const count = group?.slides.length ?? 0;

  return (
    <div className={cn("rounded-lg border border-primary/30 bg-primary/5", row.collapsed ? "p-2" : "p-4")}>
      <RowHeader
        {...row}
        id={slide.id}
        enabled={slide.enabled}
        onEnabledChange={(v) => onChange({ ...slide, enabled: v })}
        removeLabel="Remove from playlist"
        thumbnail={
          <div className="flex aspect-video items-center justify-center rounded-md border border-primary/30 bg-background">
            <Layers className="h-5 w-5 text-primary" />
          </div>
        }
        title={
          <>
            <Layers className="h-4 w-4 shrink-0 text-primary" />
            <span className="truncate">Shared group: {name}</span>
          </>
        }
        badges={
          <>
            <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
              {count} slide{count === 1 ? "" : "s"} · stays together
            </span>
            {!group && (
              <Badge variant="destructive" title="This shared group was deleted">
                missing
              </Badge>
            )}
            {slide.schedule && <Badge variant="secondary">Scheduled</Badge>}
          </>
        }
        extra={
          group && (
            <Button asChild size="sm" variant="ghost">
              <Link to={`/admin/shared-groups/${slide.sharedGroupId}`}>
                <ExternalLink className="mr-1 h-4 w-4" /> Edit group
              </Link>
            </Button>
          )
        }
      />

      {!row.collapsed && (
        <div className="mt-3 grid">
          <ScheduleEditor
            schedule={slide.schedule}
            onChange={(sch) => onChange({ ...slide, schedule: sch })}
          />
        </div>
      )}
    </div>
  );
}
